import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { X, Save, Loader2, Eye, EyeOff, Shield, Info } from 'lucide-react'
import { userService, CreateUserData, UpdateUserData } from '../../services/userService'
import { User } from '../../types'
import { extractApiErrors } from '../../utils/errorHandler'

const DEFAULT_PASSWORD = 'Viscofan2024'

interface UserFormModalProps {
  user: User | null
  onClose: () => void
  onSuccess: () => void
}

export function UserFormModal({ user, onClose, onSuccess }: UserFormModalProps) {
  const isEditing = !!user
  const [loading, setLoading] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  // Form state
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    setIsAnimating(true)
    if (user) {
      setEmail(user.email || '')
      setName(user.name)
      setIsActive(user.isActive)
      setPassword('') // Don't prefill password
    }
  }, [user])

  const handleClose = () => {
    setIsAnimating(false)
    setTimeout(onClose, 150)
  }

  const validatePassword = (pwd: string): string[] => {
    const errors: string[] = []
    if (pwd.length < 8) errors.push('Mínimo 8 caracteres')
    if (!/[A-Z]/.test(pwd)) errors.push('Una mayúscula')
    if (!/[0-9]/.test(pwd)) errors.push('Un número')
    return errors
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      toast.error('El email es requerido')
      return
    }

    if (!name.trim()) {
      toast.error('El nombre es requerido')
      return
    }

    // Validate password only if editing and password is being changed
    if (isEditing && password) {
      const passwordErrors = validatePassword(password)
      if (passwordErrors.length > 0) {
        toast.error(`Contraseña inválida: ${passwordErrors.join(', ')}`)
        return
      }
    }

    try {
      setLoading(true)

      if (isEditing) {
        const updateData: UpdateUserData = {
          email,
          name,
          isActive,
        }
        if (password) {
          updateData.password = password
        }
        await userService.update(user.id, updateData)
        toast.success('Administrador actualizado correctamente')
      } else {
        const createData: CreateUserData = {
          email,
          password: DEFAULT_PASSWORD, // Contraseña genérica
          name,
          role: 'ADMIN',
        }
        await userService.create(createData)
        toast.success('Administrador creado con contraseña genérica')
      }

      onSuccess()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0
          ? details.map(d => d.message).join('. ')
          : undefined,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-150 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div
        className={`relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden transform transition-all duration-150 ${
          isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="h-5 w-5 text-warning-500" />
            {isEditing ? 'Editar Administrador' : 'Nuevo Administrador'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-5">
            {/* Name */}
            <div>
              <label className="form-label">Nombre completo *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="Juan Pérez"
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="form-label">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="usuario@viscofan.es"
                required
              />
            </div>

            {/* Password - solo al editar */}
            {isEditing ? (
              <div>
                <label className="form-label">
                  Nueva contraseña (dejar vacío para mantener)
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pr-10"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Mínimo 8 caracteres, una mayúscula y un número
                </p>
              </div>
            ) : (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      Contraseña automática
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      El administrador recibirá la contraseña genérica <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">Viscofan2024</code> y deberá cambiarla en su primer acceso.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Role info */}
            <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="h-4 w-4 text-warning-600 dark:text-warning-400" />
                <span className="font-medium text-warning-800 dark:text-warning-300">Rol: Administrador</span>
              </div>
              <p className="text-xs text-warning-600 dark:text-warning-400">
                Los administradores pueden gestionar solicitudes, empleados y configuración del sistema.
              </p>
            </div>

            {/* Active status (only for editing) */}
            {isEditing && (
              <div className="flex items-center pt-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Usuario activo
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-slate-700/50 border-t border-gray-200 dark:border-slate-700">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary flex items-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isEditing ? 'Guardar cambios' : 'Crear administrador'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
